import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ALL_POLICIES, getPolicyById, getPoliciesForAudience, POLICY_CATEGORIES,
} from '../../compliance/policies';
import BackButtonBottom from '../../components/common/BackButtonBottom';

// ═══════════════════════════════════════════════════════════════
// COMPLIANCE POLICIES PAGE
// ═══════════════════════════════════════════════════════════════
// Two modes:
//   /compliance              → Index, grouped by category
//   /compliance/:policyId    → Single policy detail
// Visibility filtered by user role.
// ═══════════════════════════════════════════════════════════════

export default function CompliancePolicies() {
  const { policyId } = useParams();

  if (policyId) {
    return <PolicyDetail policyId={policyId} />;
  }
  return <PolicyIndex />;
}

// ───────── INDEX ─────────

function PolicyIndex() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();

  const audience = userProfile?.role || 'public';
  const visiblePolicies = getPoliciesForAudience(audience);
  const visibleIds = new Set(visiblePolicies.map(p => p.id));

  // Filter categories to only include policies visible to this user
  const visibleCategories = POLICY_CATEGORIES.map(cat => ({
    ...cat,
    policyIds: cat.policyIds.filter(id => visibleIds.has(id)),
  })).filter(cat => cat.policyIds.length > 0);

  const audienceBadge = (a) => {
    const map = {
      all: { bg: '#dbeafe', color: '#1e40af', label: 'Everyone' },
      driver: { bg: '#fef3c7', color: '#92400e', label: 'Drivers' },
      rider: { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: 'Riders' },
      internal: { bg: '#f1f5f9', color: '#5a6578', label: 'Internal' },
    };
    return map[a] || { bg: '#f1f5f9', color: '#5a6578', label: a };
  };

  return (
    <div className="max-w-[800px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate(-1)} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Back</button>
      </div>

      <h1 className="font-serif mt-0 mb-2">Compliance policies</h1>
      <p className="text-body text-[0.9rem] mb-6">
        Reservo's compliance policies governing safety, privacy, conduct, and operations.
        These documents form part of Reservo's authorisation under the NSW Point to Point Transport
        (Taxis and Hire Vehicles) Act 2016 and supplement the Terms of Use and other user agreements.
      </p>

      <div className="p-3.5 rounded-xl mb-5 text-[0.85rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
        <strong>Pre-launch notice:</strong> These policies are drafts pending legal review.
        Final versions will be published before Reservo's public launch.
      </div>

      {visibleCategories.map(cat => (
        <div key={cat.title} className="mb-7">
          <h2 className="text-[1.05rem] text-brand-dark mt-0 mb-3 pb-1.5 inline-block" style={{ borderBottom: '2px solid #2dd4a8' }}>
            {cat.title}
          </h2>
          <div className="grid gap-2.5">
            {cat.policyIds.map(id => {
              const policy = getPolicyById(id);
              if (!policy) return null;
              const badge = audienceBadge(policy.audience);
              return (
                <div key={id} onClick={() => navigate(`/compliance/${id}`)} className="p-3.5 rounded-xl bg-white border border-slate-200 cursor-pointer transition-all"
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2dd4a8'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.04)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1">
                      <div className="font-bold text-brand-dark text-[0.95rem]">{policy.title}</div>
                      <div className="text-[0.78rem] text-muted mt-0.5">
                        Version {policy.version} · Effective {policy.effectiveDate}
                      </div>
                    </div>
                    <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold whitespace-nowrap" style={{ background: badge.bg, color: badge.color }}>
                      {badge.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="mt-8 p-4 rounded-xl bg-slate-50 border border-slate-200 text-[0.82rem] text-body leading-relaxed">
        <strong>Related documents:</strong> Reservo's Terms of Use, Privacy Policy, Rider Agreement, Driver Agreement,
        and Cancellation and Refund Policy are available at the <a href="/legal" className="text-brand-primary font-semibold">/legal</a> page.
        <br /><br />
        <strong>Contact:</strong> Questions about these policies may be directed to admin@reservo.au.
      </div>

      <BackButtonBottom label="Back" />
    </div>
  );
}

// ───────── DETAIL ─────────

function PolicyDetail({ policyId }) {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const p = getPolicyById(policyId);
    if (!p) { setPolicy(null); return; }
    // Visibility check
    const audience = userProfile?.role || 'public';
    const visiblePolicies = getPoliciesForAudience(audience);
    if (!visiblePolicies.some(vp => vp.id === policyId)) {
      setAccessDenied(true);
      return;
    }
    setPolicy(p);
  }, [policyId, userProfile?.role]);

  const handlePrint = () => {
    window.print();
  };

  if (accessDenied) {
    return (
      <div className="max-w-[700px] mx-auto p-10 text-center">
        <h2 className="text-body">Access restricted</h2>
        <p className="text-muted">This policy is not available for your account type.</p>
        <button onClick={() => navigate('/compliance')} className="px-5 py-2.5 rounded-[10px] border-[1.5px] border-brand-primary bg-white text-brand-primary cursor-pointer font-semibold">← Back to policies</button>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="max-w-[700px] mx-auto p-10 text-center text-muted">
        Policy not found
      </div>
    );
  }

  const audienceBadge = (a) => {
    const map = {
      all: { bg: '#dbeafe', color: '#1e40af', label: 'For everyone' },
      driver: { bg: '#fef3c7', color: '#92400e', label: 'For drivers' },
      rider: { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: 'For riders' },
      internal: { bg: '#f1f5f9', color: '#5a6578', label: 'Internal procedure' },
    };
    return map[a] || { bg: '#f1f5f9', color: '#5a6578', label: a };
  };

  const badge = audienceBadge(policy.audience);

  return (
    <div className="max-w-[800px] mx-auto p-6">
      <div className="mb-4 no-print">
        <button onClick={() => navigate('/compliance')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← All policies</button>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-5 gap-3">
        <div>
          <h1 className="font-serif mt-0 mb-1 text-[1.6rem]">{policy.title}</h1>
          <div className="text-[0.85rem] text-muted">
            Version {policy.version} · Effective {policy.effectiveDate}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <span className="px-3 py-1 rounded-full text-[0.78rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
            {badge.label}
          </span>
          <button onClick={handlePrint} className="no-print px-3 py-1.5 rounded-lg border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.82rem]">🖨 Print</button>
        </div>
      </div>

      {/* Body */}
      <div className="policy-body p-6 rounded-xl bg-white border border-slate-200" style={{
        whiteSpace: 'pre-wrap',
        fontFamily: '"Source Sans Pro", "Segoe UI", -apple-system, sans-serif',
        fontSize: '0.92rem',
        lineHeight: 1.7,
        color: '#0a1628',
      }}>
        {policy.content}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff; }
          .policy-body { border: none !important; padding: 0 !important; font-size: 11pt !important; }
        }
      `}</style>

      <div className="no-print">
        <BackButtonBottom to="/compliance" label="All policies" />
      </div>
    </div>
  );
}